import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  File, FilePdf, FileDoc, FileXls, FileImage, UploadSimple, DownloadSimple, Trash, CircleNotch,
} from '@phosphor-icons/react';
import { DocumentUploadModal } from './DocumentUploadModal';
import {
  deleteDocument,
  fetchDocuments,
  formatFileSize,
  formatUploadDate,
  uploadDocument,
  downloadDocumentFile,
} from '../utils/documentsApi';

function FileTypeIcon({ fileType, className = 'w-3.5 h-3.5' }) {
  switch (fileType) {
    case 'PDF': return <FilePdf weight="fill" className={`${className} text-red-500`} />;
    case 'Word': return <FileDoc weight="fill" className={`${className} text-blue-500`} />;
    case 'Excel': return <FileXls weight="fill" className={`${className} text-green-600`} />;
    case 'Image': return <FileImage weight="fill" className={`${className} text-violet-500`} />;
    default: return <File weight="fill" className={`${className} text-gray-400`} />;
  }
}

export function DocumentsSection({ dealId, onSave }) {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState('');
  const [deletingId, setDeletingId] = useState(null);

  const loadDocuments = useCallback(async () => {
    if (!dealId) {
      setDocuments([]);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const docs = await fetchDocuments(dealId);
      setDocuments(docs);
    } catch (e) {
      setError(e?.message || 'Could not load documents');
      setDocuments([]);
    }
    setLoading(false);
  }, [dealId]);

  useEffect(() => { loadDocuments(); }, [loadDocuments]);

  const handleUpload = useCallback(async (file) => {
    setUploading(true);
    setUploadProgress(0);
    setUploadError('');
    try {
      const doc = await uploadDocument(dealId, file, setUploadProgress);
      setDocuments((prev) => [doc, ...prev]);
      setModalOpen(false);
      return true;
    } catch (e) {
      setUploadError(e.message || 'Upload failed');
      return false;
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  }, [dealId]);

  const handleDelete = useCallback(async (docId, fileName) => {
    if (!window.confirm(`Delete "${fileName}"?`)) return;
    setDeletingId(docId);
    try {
      await deleteDocument(dealId, docId);
      setDocuments((prev) => prev.filter((d) => d.id !== docId));
    } catch {
      setError('Delete failed');
    }
    setDeletingId(null);
  }, [dealId]);

  const handleDownload = useCallback(async (doc, e) => {
    e?.stopPropagation?.();
    try {
      await downloadDocumentFile(dealId, doc.id, doc.fileName);
    } catch {
      setError('Download failed');
    }
  }, [dealId]);

  const openPreview = useCallback((doc) => {
    navigate(`/deals/${dealId}/documents/${doc.id}`);
  }, [dealId, navigate]);

  return (
    <div className="mt-1 bg-white border border-gray-200 rounded p-1.5 md:p-1" data-testid="documents-section">
      <div className="flex items-center justify-between mb-1">
        <div className="text-[8px] md:text-[7px] font-bold text-gray-400 uppercase tracking-wide">Documents</div>
        {dealId && (
          <button
            type="button"
            onClick={() => { setUploadError(''); setModalOpen(true); }}
            className="flex items-center gap-0.5 text-[9px] md:text-[8px] font-medium text-blue-600 hover:text-blue-700 px-1.5 py-0.5 rounded hover:bg-blue-50"
            data-testid="document-upload-btn"
          >
            <UploadSimple className="w-3 h-3" weight="bold" />
            Upload document
          </button>
        )}
      </div>

      {!dealId && (
        <div className="flex flex-col sm:flex-row sm:items-center gap-1.5 py-1" data-testid="documents-save-hint">
          <p className="text-[10px] md:text-[9px] text-gray-400 italic">
            Save this analysis to attach documents.
          </p>
          {onSave && (
            <button
              type="button"
              onClick={onSave}
              className="self-start text-[10px] md:text-[9px] font-semibold text-white bg-blue-600 hover:bg-blue-700 px-2.5 py-1 min-h-[28px] rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
              data-testid="documents-save-btn"
            >
              Save analysis
            </button>
          )}
        </div>
      )}

      {dealId && loading && (
        <div className="flex items-center gap-1 py-2 text-[10px] text-gray-400">
          <CircleNotch className="w-3 h-3 animate-spin" />
          Loading documents…
        </div>
      )}

      {dealId && error && (
        <p className="text-[10px] text-red-600 py-1">{error}</p>
      )}

      {dealId && !loading && documents.length === 0 && !error && (
        <p className="text-[10px] md:text-[9px] text-gray-400 py-1">No documents attached yet.</p>
      )}

      {dealId && documents.length > 0 && (
        <div className="space-y-0.5" data-testid="documents-list">
          {documents.map((doc) => (
            <div
              key={doc.id}
              className="flex items-center gap-1.5 py-1 px-1 rounded hover:bg-gray-50 group border-b border-gray-50 last:border-0"
              data-testid={`document-row-${doc.id}`}
            >
              <FileTypeIcon fileType={doc.fileType} />
              <button
                type="button"
                onClick={() => openPreview(doc)}
                className="flex-1 min-w-0 text-left text-[10px] md:text-[9px] text-blue-700 hover:underline truncate"
                title={doc.fileName}
                data-testid={`document-name-${doc.id}`}
              >
                {doc.fileName}
              </button>
              <span className="text-[8px] text-gray-400 shrink-0 hidden sm:inline">{doc.fileType}</span>
              <span className="text-[8px] text-gray-400 shrink-0 hidden md:inline">
                {formatUploadDate(doc.uploadedAt)}
              </span>
              <span className="text-[8px] text-gray-400 shrink-0">{formatFileSize(doc.fileSize)}</span>
              <button
                type="button"
                onClick={(e) => handleDownload(doc, e)}
                className="p-0.5 text-gray-400 hover:text-blue-600 shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 rounded"
                title="Download"
                aria-label={`Download ${doc.fileName}`}
                data-testid={`document-download-${doc.id}`}
              >
                <DownloadSimple className="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                onClick={() => handleDelete(doc.id, doc.fileName)}
                disabled={deletingId === doc.id}
                className="p-0.5 text-gray-400 hover:text-red-600 shrink-0 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400 rounded"
                title="Delete"
                aria-label={`Delete ${doc.fileName}`}
                data-testid={`document-delete-${doc.id}`}
              >
                {deletingId === doc.id ? (
                  <CircleNotch className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Trash className="w-3.5 h-3.5" />
                )}
              </button>
            </div>
          ))}
        </div>
      )}

      <DocumentUploadModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        onUpload={handleUpload}
        uploading={uploading}
        progress={uploadProgress}
        error={uploadError}
      />
    </div>
  );
}
